import React, { useState } from 'react';
import * as strings from '@hydra/utils/strings';

import { makeStyles } from '@material-ui/core/styles';

import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import CriteriumSnippetElement from '@hydra/common/criterium/CriteriumSnippetElement';
import Translate from '@hydra/common/components/i18n';
import CriteriumIconAndText from '@hydra/common/criterium/CriteriumIconAndText';
import { useReviewContext } from '@hydra/common/context/review';

const MAX_AMOUNT_TO_SHOW = 10;

const useStyles = makeStyles(theme => ({
  showElements: {
    color: theme.palette.primary.darker,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  table: {
    paddingBottom: theme.spacing(3)
  },
  attributeCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

interface CriteriumAccessibilityNode {
  id: string
  accesskey: string
  snippet: string
  nodeLabel: string
  failingNodes: {
    snippet: string
    nodeLabel: string
  }[]
}

function AccessibilityTable ({ nodes, type }: { nodes: CriteriumAccessibilityNode[], type: string }) {
  const elementText = `${type}_element`.toLowerCase();
  const head = [<Translate content={elementText} key='element' />];

  const rows = nodes.map(({ snippet, nodeLabel }, i) => {
    return [<CriteriumSnippetElement key={`element-${i}`} snippet={snippet} nodeLabel={nodeLabel} />];
  });

  return <CriteriumTable head={head} rows={rows} showAmount={3} striped />;
}

function DuplicateIdActiveTable ({ nodes, type }: { nodes: CriteriumAccessibilityNode[], type: string }) {
  const classes = useStyles();
  const elementText = `${type}_element`.toLowerCase();
  const attributeText = `${type}_attribute`.toLowerCase();
  const head = [
    <Translate content={elementText} key='element-1' />,
    <Translate content={elementText} key='element-2' />,
    <Translate content={attributeText} key='attribute' />
  ];

  const rows = nodes.map(({ id, snippet, nodeLabel, failingNodes }, i) => {
    return [
      <CriteriumSnippetElement key={`element-1-${i}`} snippet={snippet} nodeLabel={nodeLabel} />,
      <CriteriumSnippetElement key={`element-2-${i}`} snippet={failingNodes[0].snippet} nodeLabel={failingNodes[0].nodeLabel} />,
      <div key='id' className={classes.attributeCell}>{`id="${id}"`}</div>
    ];
  });

  const classNames = {
    body: ['verticalAlignMiddle', 'verticalAlignMiddle', 'verticalAlignMiddle']
  };

  return (
    <CriteriumTable
      head={head}
      fixedColumns={[35, 35, 30]}
      rows={rows}
      showAmount={3}
      classNames={classNames}
      striped
    />
  );
}

function AccesskeysTable ({ nodes, type }: { nodes: CriteriumAccessibilityNode[], type: string }) {
  const classes = useStyles();
  const elementText = `${type}_element`.toLowerCase();
  const attributeText = `${type}_attribute`.toLowerCase();
  const head = [
    <Translate content={elementText} key='element-1' />,
    <Translate content={elementText} key='element-2' />,
    <Translate content={attributeText} key='attribute' />
  ];

  const rows = nodes.map(({ accesskey, snippet, nodeLabel, failingNodes }, i) => {
    return [
      <CriteriumSnippetElement key={`element-1-${i}`} snippet={snippet} nodeLabel={nodeLabel} />,
      <CriteriumSnippetElement key={`element-2-${i}`} snippet={failingNodes[0].snippet} nodeLabel={failingNodes[0].nodeLabel} />,
      <div key='accesskey' className={classes.attributeCell}>{`accesskey="${accesskey}"`}</div>
    ];
  });

  const classNames = {
    body: ['verticalAlignMiddle', 'verticalAlignMiddle', 'verticalAlignMiddle']
  };

  return (
    <CriteriumTable
      head={head}
      fixedColumns={[35, 35, 30]}
      rows={rows}
      showAmount={3}
      classNames={classNames}
      striped
    />
  );
}

const tables = {
  duplicateIdActive: DuplicateIdActiveTable,
  accesskeys: AccesskeysTable,
  tabindex: AccessibilityTable,
  headingOrder: AccessibilityTable,
  bypass: AccessibilityTable
};

interface CriteriumAccessibilityContent {
  notApplicable: boolean
    nodes: CriteriumAccessibilityNode[]
}

type CriteriumAccessibilityKeys = 'duplicateIdActive' | 'accesskeys' | 'tabindex' | 'headingOrder' | 'bypass';

export function CriteriumAccessibility ({
  data,
  type
}: {
  type: string
  data: {
    [id in CriteriumAccessibilityKeys] : CriteriumAccessibilityContent
  }
}) {
  const classes = useStyles();
  const { isPdf } = useReviewContext();

  const [expanded, setExpanded] = useState({
    duplicateIdActive: false,
    accesskeys: false,
    tabindex: false,
    headingOrder: false,
    bypass: false
  });
  function handleClick (name: CriteriumAccessibilityKeys, event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    setExpanded({
      ...expanded,
      [name]: !expanded[name]
    });
    event.stopPropagation();
  }

  const keys = Object.keys(data) as Array<CriteriumAccessibilityKeys>;
  const { notApplicable, passed, failed } = keys.reduce(
    (result, audit) => {
      const { notApplicable, nodes } = data[audit];
      if (notApplicable) {
        result.notApplicable.push({ audit });
      } else if (nodes.length === 0) {
        result.passed.push({ audit });
      } else {
        result.failed.push({ audit, nodes });
      }
      return result;
    },
    {
      notApplicable: [],
      passed: [],
      failed: []
    } as {
      notApplicable: { audit: CriteriumAccessibilityKeys }[],
      passed: { audit: CriteriumAccessibilityKeys }[],
      failed: { audit: CriteriumAccessibilityKeys, nodes: CriteriumAccessibilityNode[] }[]
    }
  );

  const count = Object.keys(data).length;
  const notApplicableMessage = <Translate content={`${type}_not_applicable`.toLowerCase()} />;
  const failedMessage = <Translate content={`${type}_summary_not_ok`.toLowerCase()} with={{ count: count }} />;
  const passedMessage = <Translate content={`${type}_summary_ok`.toLowerCase()} with={{ count: count }} />;

  return (
    <>
      {failed.length === 0 && passed.length > 0 && passedMessage}
      {failed.length > 0 && failedMessage}

      {passed.map(({ audit }, i) => {
        const content = `${type}_${strings.snakeCase(audit)}_good`.toLowerCase();
        const text = <Translate content={content} />;
        return <CriteriumIconAndText key={i} success text={text} />;
      })}

      {failed.map(({ audit, nodes }, i) => {
        const content = `${type}_${strings.snakeCase(audit)}_bad`.toLowerCase();
        const text = (
          <>
            <Translate content={content} />
            {!isPdf && (
              <div onClick={(e) => handleClick(audit, e)} className={classes.showElements}>
                <Translate content={!expanded[audit] ? 'show_elements' : 'hide_elements'} />
              </div>
            )}
          </>
        );
        const Table = tables[audit];

        return (
          <div key={i}>
            <CriteriumIconAndText success={false} text={text} />
            {
              !isPdf && expanded[audit] &&
                <div className={classes.table}>
                  <Table nodes={nodes.slice(0, MAX_AMOUNT_TO_SHOW)} type={type} />
                </div>
            }
          </div>
        );
      })}

      {notApplicable.length > 0 && notApplicableMessage}
      {notApplicable.map(({ audit }, i) => {
        const content = `${type}_${strings.snakeCase(audit)}_good`.toLowerCase();
        const text = <Translate content={content} />;
        const icon = { type: 'blank-empty' };
        return <CriteriumIconAndText key={i} icon={icon} text={text} />;
      })}
    </>
  );
}

export default CriteriumAccessibility;
